import { Machine, interpret, assign } from "xstate";
import { getSearchWidget } from "../../api/search";
import { isLocation } from "../../stores/search/search.helpers";
import { Subject, BehaviorSubject, from } from "rxjs";
import {
  debounceTime,
  tap,
  map,
  filter,
  switchMap,
  distinctUntilChanged,
} from "rxjs/operators";

export const searchInput$ = new BehaviorSubject("");

const searchInputSubscription = searchInput$
  .pipe(
    debounceTime(500),
    map((term) => ({
      type: "RETRIEVE",
      payload: {
        term,
      },
    }))
  )
  .subscribe((event) => {
    service.send(event);
  });

export const machine = Machine({
  id: "search",
  initial: "idle",
  config: null,
  context: {
    suggestions: [],
    term: "",
    widget: null,
    view: null,
  },
  states: {
    idle: {
      on: {
        INIT: {
          target: "initializing",
          actions: ["assignView", "assignConfig"],
        },
      },
    },
    initializing: {
      invoke: {
        src: "getSearchWidget",
        onDone: {
          target: "initialized",
          actions: "assignWidget",
        },
        onError: {
          target: "initialization_error",
          actions: "assignError",
        },
      },
    },
    initialization_error: {},
    initialized: {
      always: [
        {
          target: "suggestions",
        },
      ],
    },
    suggestions: {
      initial: "empty",
      on: {
        RETRIEVE: [
          {
            target: ".empty",
            cond: "termIsEmpty",
          },
          {
            target: ".search",
            actions: "assignTerm",
            cond: "isLocation",
          },
          {
            target: ".retrieving",
            actions: "assignTerm",
            cond: "termHasChanged",
          },
          {
            target: ".empty",
          },
        ],
        SEARCH: {
          target: ".search",
          actions: "assignTerm",
        },
      },
      states: {
        empty: {
          entry: "clearSuggestions",
        },
        search: {
          always: [
            {
              target: "empty",
              actions: "search",
            },
          ],
        },
        retrieving: {
          invoke: {
            src: "retrieveSuggestions",
            onDone: {
              target: "retrieved",
              actions: "assignSuggestions",
            },
            onError: {
              target: "empty",
              actions: "assignError",
            },
          },
        },
        retrieved: {},
      },
    },
  },
});

const config = {
  guards: {
    termIsEmpty: (_, event) => event.payload.term.length === 0,
    isLocation: (_, event) => isLocation(event.payload.term),
    termHasChanged: (context, event) => context.term !== event.payload.term,
  },
  actions: {
    assignTerm: assign({
      term: (_, event) => event.payload.term,
    }),
    assignError: assign((_, event) => ({
      error: event.data,
    })),
    assignWidget: assign((_, event) => ({
      widget: event.data,
    })),
    assignConfig: assign((_, event) => ({
      config: event.payload.config,
    })),
    assignView: assign((_, event) => ({
      view: event.payload.view,
    })),
    assignSuggestions: assign((_, event) => ({
      suggestions: event.data,
    })),
    clearSuggestions: assign((context, _) => ({
      suggestions: [],
    })),
    search: (context, _) => context.widget.search(context.term),
  },
  services: {
    retrieveSuggestions: (context, _) => {
      const { widget } = context;
      const { term } = context;
      return widget
        .suggest(term)
        .then((data) => {
          return data;
        })
        .then((data) => data.results[1].results)
        .catch((error) => error);
    },
    getSearchWidget: (context, _) =>
      getSearchWidget({ view: context.view, config: context.config }),
  },
};

export const service = interpret(machine.withConfig(config)).start();

service.onStop(() => {
  searchInputSubscription.unsubscribe();
});
